import { Action } from 'typed/actions';
import {
    ERPAdminCostDivisionType, ERPDimensionSyncType, IntegrationDataMappingDimension, IntegrationDataMappingMatchRule, IntegrationDataMappingSource,
    RequestStatus, defaults, entities
} from 'typed/api';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer, entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer,
    entityLoadAllReducer, entityLoadReducer, entityVoidAllReducer, networkActionReducer, sortableEntitiesReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { deepAssign } from 'typed/utils';

const
    adn = defaults.NetworkArrayOf(defaults.AccountingData),
    adpn = defaults.NetworkArrayOf(defaults.AccountingDataPreviewReport),
    pian = defaults.PerformanceIndicatorAccountNumberArray,
    idmrn = defaults.NetworkArrayOf(defaults.IntegrationDataMappingRule),
    idpn = defaults.NetworkArrayOf(defaults.InvoicingDataPreviewReport),
    edm = defaults.ERPDimensionMapping,
    eacdr = defaults.ERPAdminCostDivisionRuleReport,
    eacdrn = defaults.NetworkArrayOf(eacdr),
    eacdrfaa: entities.ERPAdminCostDivisionRuleFromAccount[] = [],
    eacdrfra: entities.ERPAdminCostDivisionRuleFixedRule[] = [],
    eacdrla: entities.ERPAdminCostDivisionRuleLevel[] = [],
    ecan = defaults.NetworkArrayOf(defaults.ERPCorporateAccount),
    edssn = defaults.NetworkArrayOf(defaults.ERPDimensionSyncSetting);

export default combineReducers({
    manualAccountingData: switchReducers(
        adn,
        entityLoadAllReducer(adn, 'LOAD_ALL_MANUAL_ACCOUNTING_DATA'),
        entityEditAllReducer(adn, 'EDIT_ALL_ACCOUNTING_DATAS'),
        entityDeleteAllReducer(adn, 'DELETE_ALL_ACCOUNTING_DATAS'),
        (state: entities.NetworkArray<entities.AccountingData> = adn, action: Action<'ADD_ALL_ACCOUNTING_DATAS'>) =>
            action.type === 'ADD_ALL_ACCOUNTING_DATAS' && action.payload.status === RequestStatus.SUCCEEDED ?
                deepAssign(state, {
                    Custom: {
                        status: RequestStatus.UNSYNCED
                    }
                }) :
                state
    ),
    accountingDataPreviewReport: switchReducers(
        adpn,
        networkActionReducer(adpn, 'LOAD_ACCOUNTING_DATA_PREVIEW_REPORT'),
        (state: entities.NetworkArray<entities.AccountingDataPreviewReport> = adpn, action: Action<'LOAD_ACCOUNTING_DATA_PREVIEW_REPORT'>) =>
            action.type === 'LOAD_ACCOUNTING_DATA_PREVIEW_REPORT' && action.payload.status === RequestStatus.SUCCEEDED ?
                deepAssign(state, {
                    Data: action.payload.offset === 0 ? action.payload.response : state.Data.concat(action.payload.response)
                }) :
                state
    ),
    indicatorAccounts: switchReducers(
        pian,
        networkActionReducer(pian, 'GET_USED_ACCOUNTS_FROM_ACCOUNT_RANGE'),
        (state: entities.PerformanceIndicatorAccountNumberArray = pian, action: Action<'GET_USED_ACCOUNTS_FROM_ACCOUNT_RANGE'>) => {
            let newState = state;

            if (action.type === 'GET_USED_ACCOUNTS_FROM_ACCOUNT_RANGE') {
                if (action.payload.status === RequestStatus.PENDING) {
                    newState = deepAssign(newState, {
                        Custom: {
                            indicatorID: action.payload.performanceIndicatorID
                        }
                    });
                }

                if (action.payload.status === RequestStatus.SUCCEEDED) {
                    newState = deepAssign(newState, { Data: action.payload.response });
                }
            }

            return newState as entities.PerformanceIndicatorAccountNumberArray;
        }
    ),
    integrationDataMappingRules: switchReducers(
        idmrn,
        entityLoadAllReducer(idmrn, 'LOAD_ALL_INTEGRATION_DATA_MAPPING_RULES'),
        entityAddReducer(idmrn, 'ADD_INTEGRATION_DATA_MAPPING_RULE'),
        entityEditReducer(idmrn, 'EDIT_INTEGRATION_DATA_MAPPING_RULE'),
        entityDeleteReducer(idmrn, 'DELETE_INTEGRATION_DATA_MAPPING_RULE')
    ),
    integrationDataMappingDimensions: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.IntegrationDataMappingDimension),
        'LOAD_ALL_INTEGRATION_DATA_MAPPING_DIMENSIONS',
        IntegrationDataMappingDimension as any,
        'IntegrationDataMappingDimension'
    ),
    integrationDataMappingMatchRules: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.IntegrationDataMappingMatchRule),
        'LOAD_ALL_INTEGRATION_DATA_MAPPING_MATCH_RULES',
        IntegrationDataMappingMatchRule as any,
        'IntegrationDataMappingMatchRule'
    ),
    integrationDataMappingSources: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.IntegrationDataMappingSource),
        'LOAD_ALL_INTEGRATION_DATA_MAPPING_SOURCES',
        IntegrationDataMappingSource as any,
        'IntegrationDataMappingSource'
    ),
    invoicingDataPreviewReport: switchReducers(
        idpn,
        networkActionReducer(idpn, 'LOAD_INVOICING_DATA_PREVIEW_REPORT'),
        (state: entities.NetworkArray<entities.InvoicingDataPreviewReport> = idpn, action: Action<'LOAD_INVOICING_DATA_PREVIEW_REPORT'>) =>
            action.type === 'LOAD_INVOICING_DATA_PREVIEW_REPORT' && action.payload.status === RequestStatus.SUCCEEDED ?
                deepAssign(state, {
                    Data: action.payload.offset === 0 ? action.payload.response : state.Data.concat(action.payload.response)
                }) :
                state
    ),
    erpDimensionMapping: switchReducers(
        edm,
        networkActionReducer(edm, 'LOAD_ERP_DIMENSION_MAPPING'),
        networkActionReducer(edm, 'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION'),
        networkActionReducer(edm, 'DISABLE_ERP_DIMENSION_MAPPING_DIMENSION'),
        (state, action: Action<'LOAD_ERP_DIMENSION_MAPPING' | 'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION' | 'DISABLE_ERP_DIMENSION_MAPPING_DIMENSION'>) => {
            const newState = state || edm;

            if (action.payload && action.payload.status === RequestStatus.SUCCEEDED) {
                if (action.type === 'LOAD_ERP_DIMENSION_MAPPING' || action.type === 'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION') {
                    return action.payload.response;
                }

                if (action.type === 'DISABLE_ERP_DIMENSION_MAPPING_DIMENSION') {
                    return deepAssign(newState, {
                        [`Dimension${action.payload.dimension}SyncHandleTypeID`]: ERPDimensionSyncType.NO_ACTION,
                        [`Dimension${action.payload.dimension}SyncTargetRegisterID`]: null
                    });
                }
            }

            return newState;
        },
        entityVoidAllReducer(edm, 'VOID_ERP_DIMENSION_MAPPING')
    ),
    erpAdminCostDivisionRules: sortableEntitiesReducer(eacdrn, null, switchReducers(
        eacdrn,
        entityLoadAllReducer(eacdrn, 'LOAD_ALL_ERP_ADMIN_COST_DIVISION_RULES'),
        entityLoadReducer(eacdrn, 'LOAD_ERP_ADMIN_COST_DIVISION_RULE'),
        entityAddReducer(eacdrn, 'ADD_ERP_ADMIN_COST_DIVISION_RULE'),
        entityEditReducer(eacdrn, 'EDIT_ERP_ADMIN_COST_DIVISION_RULE'),
        childrenReducer(
            eacdrn,
            combineReducers(eacdr, {
                SourceFilter: entityEditReducer(defaults.ERPAdminCostDivisionRuleSourceFilter, 'EDIT_ERP_ADMIN_COST_DIVISION_RULE_SOURCE_FILTER'),
                FromAccounts: sortedEntitiesReducer(eacdrfaa, 'FromAccountNumber', switchReducers(
                    eacdrfaa,
                    entityAddAllReducer(eacdrfaa, 'ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FROM_ACCOUNTS'),
                    entityDeleteAllReducer(eacdrfaa, 'DELETE_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FROM_ACCOUNTS')
                )),
                FixedRules: switchReducers(
                    eacdrfra,
                    entityAddAllReducer(eacdrfra, 'ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES'),
                    entityEditAllReducer(eacdrfra, 'EDIT_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES'),
                    entityDeleteAllReducer(eacdrfra, 'DELETE_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_FIXED_RULES')
                ),
                Levels: switchReducers(
                    eacdrla,
                    entityAddAllReducer(eacdrla, 'ADD_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_LEVELS'),
                    entityEditAllReducer(eacdrla, 'EDIT_ALL_ERP_ACCOUNT_COST_DIVISION_RULE_LEVELS')
                )
            }),
            (state, action) => state.Data.getIndexByID(action.payload && action.payload.parentID)
        ),
        entityDeleteReducer(eacdrn, 'DELETE_ERP_ADMIN_COST_DIVISION_RULE'),
        (state, action) => {
            const defaultState = state || eacdrn;

            if (action.type === 'DELETE_PRODUCT' ||
                action.type === 'DELETE_GEO_AREA' ||
                action.type === 'DELETE_CUSTOMER' ||
                action.type === 'DELETE_TRACKING_LEVEL_1_ITEM' ||
                action.type === 'DELETE_TRACKING_LEVEL_2_ITEM' ||
                action.type === 'DELETE_TRACKING_LEVEL_3_ITEM' ||
                action.type === 'DELETE_TRACKING_LEVEL_4_ITEM' ||
                action.type === 'DELETE_COMPANY_LEGAL_ENTITY' ||
                action.type === 'DELETE_COMPANY_FUNCTION') {
                return entityVoidAllReducer(eacdrn, action.type)(state, action);
            }

            return defaultState;
        }
    )),
    erpAdminCostDivisionTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.ERPAdminCostDivisionType),
        'LOAD_ALL_ERP_ADMIN_COST_DIVISION_TYPES',
        ERPAdminCostDivisionType as any,
        'ERPAdminCostDivisionType'
    ),
    erpCorporateAccounts: switchReducers(
        ecan,
        entityLoadAllReducer(ecan, 'LOAD_ALL_ERP_CORPORATE_ACCOUNTS'),
        entityAddAllReducer(ecan, 'ADD_ALL_ERP_CORPORATE_ACCOUNTS'),
        entityEditAllReducer(ecan, 'EDIT_ALL_ERP_CORPORATE_ACCOUNTS'),
        entityDeleteAllReducer(ecan, 'DELETE_ALL_ERP_CORPORATE_ACCOUNTS')
    ),
    erpDimensionSyncSettings: switchReducers(edssn,
        entityLoadAllReducer(edssn, 'LOAD_ALL_ERP_DIMENSION_SYNC_SETTINGS'),
        entityAddAllReducer(edssn, 'ADD_ALL_ERP_DIMENSION_SYNC_SETTINGS'),
        entityEditAllReducer(edssn, 'EDIT_ALL_ERP_DIMENSION_SYNC_SETTINGS'),
        entityDeleteAllReducer(edssn, 'DELETE_ALL_ERP_DIMENSION_SYNC_SETTINGS')
    )
});
